import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Check, ShieldCheck, Recycle, Sun, Layers, Truck, Award, Mail, Loader2, Gauge, TrendingDown } from "lucide-react";
import { PRODUCTS, IMAGES } from "../lib/data";
import { PageHero, CTABand, FaqSection } from "../components/site/Bits";
import { Reveal } from "../components/site/Reveal";
import { Seo } from "../components/site/Seo";
import api, { formatApiErrorDetail } from "../lib/api";
import { getVisitorId, getAttribution, trackEvent } from "../lib/track";

const bbm = PRODUCTS.find((p) => p.slug === "big-bad-mats");

const MATERIAL = [
  { icon: Layers, title: "Best-in-class HDPE composite", desc: "Engineered high-density polyethylene that flexes under load without cracking, warping, or splintering like timber." },
  { icon: Truck, title: "Rated for the heaviest loads", desc: "Built to carry cranes, drill rigs, and fully loaded haul trucks across soft, uneven, and sensitive ground." },
  { icon: Recycle, title: "Reusable for years, not one job", desc: "Dozens of deployment cycles per mat — driving cost-per-use far below single-use gravel, timber, or plywood." },
  { icon: Sun, title: "Weather & chemical resistant", desc: "Won't rot, absorb water, or degrade in UV and heat. The same performance in year five as day one." },
  { icon: ShieldCheck, title: "Non-slip, interlocking safety", desc: "A stable, slip-resistant surface that installs and picks up fast with standard equipment — safer footing for crews." },
  { icon: Award, title: "Near-zero site restoration", desc: "Protects turf, topsoil, wetlands, and waterways so the ground is left clean, intact, and restoration-ready." },
];

const MAT_INDUSTRIES = [
  { id: "pipeline", name: "Pipeline Construction", useCase: "Continuous access roads and work platforms along the right-of-way — over soft soils, wetlands, and stream crossings." },
  { id: "oil-gas", name: "Oil & Gas", useCase: "Stable pads for drilling rigs, workover units, and heavy equipment across lease roads and well sites." },
  { id: "construction", name: "Heavy & Civil Construction", useCase: "Temporary haul roads, crane pads, and laydown areas that keep equipment moving in any weather." },
  { id: "utility", name: "Utility Contractors", useCase: "Protected access to poles, pads, and easements while keeping turf, landscaping, and private property intact." },
  { id: "hdd", name: "Directional Drilling (HDD)", useCase: "Firm, level platforms for drill rigs and mud systems, plus protected entry and exit pits." },
  { id: "telecom", name: "Telecom & Fiber", useCase: "Low-impact access across residential streets and greenfield routes for cabling and boring crews." },
  { id: "municipal", name: "Municipal & Public Works", useCase: "Reusable matting for parks, events, storm response, and public-ROW projects that must be restored spotless." },
];

const MAT_PERSONAS = [
  { id: "pm", name: "Project Manager / Superintendent", value: "Big Bad Mats deploy fast and stay put, so weather and soft ground stop costing you days. You get predictable access, fewer stuck-equipment delays, and a jobsite that's ready the moment your crews are." },
  { id: "procurement", name: "Procurement / Purchasing", value: "One accountable source for buy-or-rent matting at the lowest cost per use — reusable composite that replaces disposable gravel and timber, slashes restoration spend, and gives you predictable, defensible pricing." },
  { id: "safety", name: "Safety / EHS", value: "Slip-resistant, stable, load-rated surfaces cut trip, tip, and struck-by risk — while protecting soil, wetlands, and waterways to keep you ahead of restoration and environmental compliance requirements." },
  { id: "ops", name: "Operations / Field Crew", value: "Interlocking mats install and pick up fast with standard equipment, take repeated heavy passes, and won't warp or splinter like timber — so crews spend time on the work, not fighting the ground." },
  { id: "owner", name: "Owner / Executive", value: "Protect the landowner's asset and your reputation with near-zero site restoration, while reusable, best-in-class matting protects margin project after project — an investment that keeps paying back." },
];

function ValueMatrix() {
  const [ind, setInd] = useState(MAT_INDUSTRIES[0].id);
  const [per, setPer] = useState(MAT_PERSONAS[0].id);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const industry = MAT_INDUSTRIES.find((i) => i.id === ind);
  const persona = MAT_PERSONAS.find((p) => p.id === per);
  const key = `${ind}-${per}`;

  const emailBrief = async (e) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Please enter a valid work email.");
      return;
    }
    setSending(true);
    try {
      await api.post("/premium-mats/value-prop", {
        email, name,
        industry: industry.name, persona: persona.name,
        use_case: industry.useCase, value_prop: persona.value,
        site_url: window.location.origin,
        visitor_id: getVisitorId(), attribution: getAttribution(),
      });
      trackEvent("cta_click", { label: `Value brief: ${industry.name} / ${persona.name}`, product: "Big Bad Mats" });
      setDone(true);
      toast.success("Sent — check your inbox for your value brief.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="value-matrix" className="mt-10 grid gap-8 lg:grid-cols-[320px_1fr]">
      {/* Filters */}
      <div className="space-y-8">
        <div>
          <div className="kicker text-flame mb-3">1 · Your industry</div>
          <div className="flex flex-wrap gap-2">
            {MAT_INDUSTRIES.map((i) => {
              const on = i.id === ind;
              return (
                <button
                  key={i.id}
                  type="button"
                  data-testid={`matrix-industry-${i.id}`}
                  onClick={() => { setInd(i.id); setDone(false); }}
                  aria-pressed={on}
                  className={`px-3.5 py-2 text-xs font-semibold border transition-colors duration-200 ${on ? "bg-flame text-white border-flame" : "bg-transparent text-ink border-border hover:border-flame/60 hover:text-flame"}`}
                >
                  {i.name}
                </button>
              );
            })}
          </div>
        </div>
        <div>
          <div className="kicker text-flame mb-3">2 · Your role</div>
          <div className="flex flex-wrap gap-2">
            {MAT_PERSONAS.map((p) => {
              const on = p.id === per;
              return (
                <button
                  key={p.id}
                  type="button"
                  data-testid={`matrix-persona-${p.id}`}
                  onClick={() => { setPer(p.id); setDone(false); }}
                  aria-pressed={on}
                  className={`px-3.5 py-2 text-xs font-semibold border transition-colors duration-200 ${on ? "bg-flame text-white border-flame" : "bg-transparent text-ink border-border hover:border-flame/60 hover:text-flame"}`}
                >
                  {p.name}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Result */}
      <div className="relative border border-border bg-card p-8 lg:p-10 min-h-[340px]">
        <AnimatePresence mode="wait">
          <motion.div
            key={key}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.25, 1, 0.5, 1] }}
          >
            <div className="kicker text-flame">{industry.name} · {persona.name}</div>
            <h3 className="mt-3 font-display font-bold text-2xl md:text-3xl text-ink tracking-tight">Your use case</h3>
            <p className="mt-3 text-lg text-muted-foreground leading-relaxed">{industry.useCase}</p>

            <h3 className="mt-8 font-display font-bold text-2xl md:text-3xl text-ink tracking-tight">Why Big Bad Mats for you</h3>
            <p className="mt-3 text-lg text-muted-foreground leading-relaxed">{persona.value}</p>

            <div className="mt-8 flex flex-wrap gap-2.5" data-testid="matrix-proof">
              {["Best-in-class HDPE composite", "Rated for the heaviest loads", "Lowest cost per use", "Near-zero restoration"].map((c) => (
                <span key={c} className="inline-flex items-center gap-1.5 bg-flame/[0.07] text-ink text-xs font-medium px-3 py-1.5">
                  <Check className="h-3.5 w-3.5 text-flame" /> {c}
                </span>
              ))}
            </div>

            <Link
              to="/request-quote"
              data-testid="matrix-cta"
              className="mt-8 inline-flex items-center gap-2 bg-ink text-white px-6 py-3 font-semibold hover:bg-flame transition-colors"
            >
              Get a Big Bad Mats quote <ArrowRight className="h-4 w-4" />
            </Link>

            {/* Email-me-this-brief lead capture */}
            <div className="mt-8 border-t border-border pt-6" data-testid="matrix-email-capture">
              {done ? (
                <div className="flex items-start gap-3">
                  <div className="h-9 w-9 bg-emerald-100 text-emerald-600 flex items-center justify-center flex-shrink-0"><Check className="h-4.5 w-4.5" /></div>
                  <div>
                    <p className="font-semibold text-ink">Your value brief is on the way.</p>
                    <p className="text-sm text-muted-foreground">We've emailed the one-page PDF for <strong>{industry.name} · {persona.name}</strong> to <strong>{email}</strong>.</p>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2 text-flame"><Mail className="h-4 w-4" /><span className="kicker">Take it with you</span></div>
                  <p className="mt-2 text-sm text-muted-foreground">Email yourself this value brief as a one-page PDF you can forward to your team.</p>
                  <form onSubmit={emailBrief} data-testid="matrix-email-form" className="mt-4 flex flex-col sm:flex-row gap-2.5" noValidate>
                    <input
                      data-testid="matrix-email-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Name (optional)"
                      className="w-full sm:w-36 border border-border bg-background px-3.5 py-2.5 text-sm text-ink placeholder:text-muted-foreground/60 focus:outline-none focus:ring-1 focus:border-flame focus:ring-flame"
                    />
                    <input
                      data-testid="matrix-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Work email *"
                      className="flex-1 border border-border bg-background px-3.5 py-2.5 text-sm text-ink placeholder:text-muted-foreground/60 focus:outline-none focus:ring-1 focus:border-flame focus:ring-flame"
                    />
                    <button data-testid="matrix-email-submit" disabled={sending}
                      className="inline-flex items-center justify-center gap-2 bg-flame text-white px-5 py-2.5 text-sm font-semibold hover:bg-flame/90 disabled:opacity-50 transition-colors whitespace-nowrap">
                      {sending ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending</> : <>Email me this brief</>}
                    </button>
                  </form>
                </>
              )}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}

function RoiCalculator() {
  const [uses, setUses] = useState(20);
  const MAT_PRICE = 1200; // illustrative one-time purchase per mat
  const TIMBER_PER_USE = 320; // single-use timber: buy + haul + restoration
  const GRAVEL_PER_USE = 480; // gravel pad build-up + removal + restoration
  const bbmPerUse = MAT_PRICE / uses;
  const scale = MAT_PRICE; // fixed scale so bars tell the amortization story
  const savings = Math.round(((TIMBER_PER_USE - bbmPerUse) / TIMBER_PER_USE) * 100);

  const rows = [
    { label: "Big Bad Mats (reusable)", value: bbmPerUse, flame: true },
    { label: "Single-use timber cribbing", value: TIMBER_PER_USE },
    { label: "Gravel pad (build & restore)", value: GRAVEL_PER_USE },
  ];

  return (
    <div className="mt-10 grid gap-8 lg:grid-cols-[1fr_1.1fr] items-start" data-testid="roi-calculator">
      <div className="border border-border bg-card p-8">
        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold text-ink">Deployments (uses)</span>
          <span className="font-display font-extrabold text-3xl text-flame" data-testid="roi-uses">{uses}</span>
        </div>
        <input
          type="range" min="1" max="50" value={uses}
          onChange={(e) => setUses(Number(e.target.value))}
          data-testid="roi-slider"
          className="mt-4 w-full accent-flame cursor-pointer"
        />
        <div className="mt-2 flex justify-between text-xs text-muted-foreground"><span>1 use</span><span>50 uses</span></div>

        <div className="mt-8 bg-flame/[0.07] p-5">
          <div className="kicker text-flame">Big Bad Mats effective cost / use</div>
          <div className="mt-1 font-display font-extrabold text-4xl text-ink" data-testid="roi-cost-per-use">${bbmPerUse.toFixed(0)}</div>
          {savings > 0 && (
            <div className="mt-1 inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-600">
              <TrendingDown className="h-4 w-4" /> {savings}% lower than single-use timber
            </div>
          )}
        </div>
        <p className="mt-4 text-xs text-muted-foreground leading-relaxed">Illustrative unit economics. A reusable mat's cost is spread across every deployment, so cost-per-use keeps falling — while disposable options are re-bought (and re-restored) every single time. Actual pricing varies by mat type, volume, rent vs. buy, and project. <Link to="/request-quote" className="text-flame font-semibold">Ask us for numbers on your project.</Link></p>
      </div>

      <div className="border border-border bg-card p-8">
        <div className="kicker text-flame">Cost per use at {uses} deployment{uses > 1 ? "s" : ""}</div>
        <div className="mt-6 space-y-5">
          {rows.map((r) => (
            <div key={r.label}>
              <div className="flex justify-between text-sm mb-1.5">
                <span className={`font-medium ${r.flame ? "text-ink" : "text-muted-foreground"}`}>{r.label}</span>
                <span className={`font-display font-bold ${r.flame ? "text-flame" : "text-ink"}`}>${r.value.toFixed(0)}/use</span>
              </div>
              <div className="h-3 bg-muted overflow-hidden">
                <motion.div
                  className={r.flame ? "h-full bg-flame" : "h-full bg-charcoal/40"}
                  animate={{ width: `${Math.min(100, (r.value / scale) * 100)}%` }}
                  transition={{ duration: 0.4, ease: [0.25, 1, 0.5, 1] }}
                />
              </div>
            </div>
          ))}
        </div>
        <div className="mt-8 grid grid-cols-3 gap-4 border-t border-border pt-6 text-center">
          {[["500+ tons", "Load capacity per mat"], ["Years", "Service life, not one job"], ["Dozens", "Reuse cycles per mat"]].map(([a, b]) => (
            <div key={a}>
              <div className="font-display font-extrabold text-xl text-flame">{a}</div>
              <div className="text-xs text-muted-foreground mt-1 leading-tight">{b}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function PremiumMats() {
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Product",
      name: "Big Bad Mats",
      description: "Best-in-class HDPE composite ground protection matting — engineered for the heaviest loads, years of reuse, and near-zero site restoration.",
      image: "https://oneharvestco.com/og-share.jpg",
      category: "Ground Protection Systems",
      brand: { "@type": "Brand", name: "OneHarvest" },
      url: "https://oneharvestco.com/premium-mats",
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://oneharvestco.com/" },
        { "@type": "ListItem", position: 2, name: "Products", item: "https://oneharvestco.com/solutions" },
        { "@type": "ListItem", position: 3, name: "Premium Mats", item: "https://oneharvestco.com/premium-mats" },
      ],
    },
    ...(bbm?.faqs?.length
      ? [{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: bbm.faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
        }]
      : []),
  ];

  return (
    <div data-testid="premium-mats-page">
      <Seo
        title="Premium Mats — Big Bad Mats"
        description="Big Bad Mats: best-in-class HDPE composite ground protection matting built for the heaviest loads and years of reuse. Find your industry- and role-specific value proposition."
        jsonLd={jsonLd}
      />

      <PageHero
        kicker="Premium Mats · Flagship Big Bad Mats"
        title="The last mat your jobsite will need."
        subtitle="Big Bad Mats are best-in-class HDPE composite ground protection — engineered for the heaviest equipment, built to be reused for years, and designed to leave the ground clean and restoration-ready."
        image={IMAGES.bigbadmats}
      />

      {/* Material & longevity */}
      <section className="bg-background" data-testid="premium-material">
        <div className="container-x py-24">
          <div className="max-w-3xl">
            <div className="kicker text-flame">Material & Longevity</div>
            <h2 className="mt-2 font-display font-bold text-3xl md:text-4xl text-ink tracking-tight">Built from a better material. Built to outlast the job.</h2>
            <p className="mt-4 text-lg text-muted-foreground leading-relaxed">Timber cracks, gravel disappears into the mud, and plywood is a one-project expense. Big Bad Mats are engineered composite — the material difference you feel on the first deployment and every one after.</p>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {MATERIAL.map((m, i) => {
              const Icon = m.icon;
              return (
                <Reveal key={m.title} delay={(i % 3) * 0.06}>
                  <div className="h-full border border-border bg-card p-6 hover:border-flame/50 hover:-translate-y-1 transition-[transform,border-color] duration-300">
                    <div className="h-11 w-11 flex items-center justify-center bg-flame/10 text-flame">
                      <Icon className="h-5 w-5" strokeWidth={1.9} />
                    </div>
                    <h3 className="mt-4 font-display font-bold text-lg text-ink">{m.title}</h3>
                    <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{m.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Durability, strength & long-term ROI */}
      <section className="bg-charcoal text-white" data-testid="premium-roi">
        <div className="container-x py-24">
          <div className="grid gap-10 lg:grid-cols-2 lg:items-center">
            <div>
              <div className="kicker text-flame">Strength · Durability · ROI</div>
              <h2 className="mt-2 font-display font-bold text-3xl md:text-4xl tracking-tight">Buy once. Deploy for years. Watch the cost per use collapse.</h2>
              <p className="mt-4 text-lg text-white/70 leading-relaxed">Big Bad Mats are engineered composite built to carry the heaviest equipment and shrug off weather, chemicals, and repeated heavy passes — without cracking, warping, or rotting like timber. That durability is the whole ROI story: because one mat is reused across dozens of deployments, its cost is spread thinner every single time it hits the ground.</p>
              <div className="mt-6 space-y-3">
                {["Rated for cranes, drill rigs, and fully loaded haul trucks — real jobsite strength.",
                  "HDPE composite that won't splinter, rot, or absorb water — the same performance in year five.",
                  "Every reuse divides the purchase cost further — the math that timber and gravel can never match."].map((t) => (
                  <div key={t} className="flex items-start gap-3 text-white/85">
                    <ShieldCheck className="h-5 w-5 text-flame flex-shrink-0 mt-0.5" strokeWidth={1.9} />
                    <span>{t}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-3 text-white/80">
              <Gauge className="h-6 w-6 text-flame" />
              <span className="font-display font-bold text-xl">Run your own unit economics</span>
            </div>
          </div>
          <div className="[&_.text-ink]:text-white [&_.bg-card]:bg-ink [&_.border-border]:border-white/10 [&_.bg-muted]:bg-white/10 [&_.text-muted-foreground]:text-white/60">
            <RoiCalculator />
          </div>
        </div>
      </section>

      {/* Value proposition matrix */}
      <section className="bg-muted/40 border-y border-border" data-testid="premium-matrix">
        <div className="container-x py-24">
          <div className="max-w-3xl">
            <div className="kicker text-flame">Find Your Value</div>
            <h2 className="mt-2 font-display font-bold text-3xl md:text-4xl text-ink tracking-tight">See exactly what Big Bad Mats do for <span className="text-flame">you</span>.</h2>
            <p className="mt-4 text-lg text-muted-foreground leading-relaxed">Pick your industry and your role — and get the use case and value proposition that matches how you actually work.</p>
          </div>
          <ValueMatrix />
        </div>
      </section>

      <FaqSection faqs={bbm?.faqs} title="Big Bad Mats — frequently asked questions" />

      <CTABand />
    </div>
  );
}
